import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, message } from "antd";
import type { InputRef } from "antd";
import { CalendarOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { API } from "../apiUrl";
import { getToken } from "./AuthPanel";
import { dateKeyToApiDate, dayIndex, todayKey } from "../services/plan";

export interface ShareItem {
  id: string;
  author: string;
  text: string;
  at: number;
  replyTo?: string;
  quote?: string;
}

interface CheckinUser {
  user_id: string;
  name: string;
}

interface Props {
  dateKey: string;
  author: string | null;
}

const CheckinPanel: React.FC<Props> = ({ dateKey, author }) => {
  const intl = useIntl();
  const [checkins, setCheckins] = useState<CheckinUser[]>([]);
  const [checkinCount, setCheckinCount] = useState(0);
  const [shares, setShares] = useState<ShareItem[]>([]);
  const [meCheckedIn, setMeCheckedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [quoteTarget, setQuoteTarget] = useState<ShareItem | null>(null);
  const shareInputRef = useRef<InputRef>(null);

  const fetchData = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/api/checkins?date=${encodeURIComponent(dateKeyToApiDate(dateKey))}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        checkins?: CheckinUser[];
        count?: number;
        shares?: ShareItem[];
        meCheckedIn?: boolean;
      };
      setCheckins(data.checkins || []);
      setCheckinCount(data.count ?? data.checkins?.length ?? 0);
      setShares(data.shares || []);
      setMeCheckedIn(!!data.meCheckedIn);
    } catch {
      // 拉取失败时保留当前数据，不打断阅读
    }
  }, [dateKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchData().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchData, author]);

  const post = useCallback(
    async (payload: { dateKey: string; text?: string; replyTo?: string }, successId: string): Promise<boolean> => {
      const token = getToken();
      if (!token) {
        message.info(intl.formatMessage({ id: "auth.benefits" }));
        return false;
      }
      try {
        const res = await fetch(`${API}/api/checkin`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...payload, dateKey: dateKeyToApiDate(payload.dateKey) }),
        });
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        message.success(intl.formatMessage({ id: successId }));
        fetchData();
        return true;
      } catch {
        message.error(intl.formatMessage({ id: "common.error" }));
        return false;
      }
    },
    [intl, fetchData]
  );

  const handleCheckin = useCallback(() => {
    void post({ dateKey }, "reading.checkinDone");
  }, [dateKey, post]);

  const handleShare = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    void post({ dateKey, text: trimmed, replyTo: quoteTarget?.id }, "reading.shareSubmit").then((ok) => {
      if (ok) {
        setText("");
        setQuoteTarget(null);
      }
    });
  }, [text, dateKey, post, quoteTarget]);

  const startQuote = useCallback(
    (target: ShareItem) => {
      if (!author) {
        message.info(intl.formatMessage({ id: "auth.benefits" }));
        return;
      }
      setQuoteTarget(target);
      setTimeout(() => shareInputRef.current?.focus(), 0);
    },
    [author, intl]
  );

  // 未来日期还没读到：打卡/分享整块不显示
  const isFuture = dayIndex(dateKey) > dayIndex(todayKey());
  if (isFuture) return null;

  // 全站还没有任何记录时，未登录用户只看到登录引导
  if (!loading && !author && checkinCount === 0 && shares.length === 0) {
    return (
      <div className="checkin-panel checkin-panel-login-hint">
        {intl.formatMessage({ id: "auth.benefits" })}
      </div>
    );
  }

  const loggedIn = !!author;

  return (
    <div className="checkin-panel">
      <div className="checkin-row">
        <Button
          type={meCheckedIn ? "default" : "primary"}
          icon={meCheckedIn ? <CheckCircleFilled /> : <CalendarOutlined />}
          disabled={meCheckedIn || !loggedIn}
          title={!loggedIn ? intl.formatMessage({ id: "auth.benefits" }) : undefined}
          onClick={handleCheckin}
        >
          {intl.formatMessage({ id: meCheckedIn ? "reading.checkinRead" : "reading.checkin" })}
        </Button>
        {checkinCount > 0 && (
          <span className="checkin-people">
            {intl.formatMessage({ id: "reading.checkinPeople" }, { count: checkinCount })}
          </span>
        )}
      </div>
      {checkins.length > 0 && (
        <div className="checkin-people-list">
          {checkins.map((u) => (
            <span key={u.user_id} className="checkin-person">
              {u.name}
            </span>
          ))}
        </div>
      )}

      <div className="shares-list">
        {shares.length === 0 ? (
          <div style={{ color: "#999", fontSize: 13 }}>{intl.formatMessage({ id: "reading.noShares" })}</div>
        ) : (
          shares.map((s) => (
            <div key={s.id} className="share-item">
              <div className="share-meta">
                <span>
                  {s.author} · {intl.formatDate(new Date(s.at), { dateStyle: "medium", timeStyle: "short" })}
                </span>
                <button type="button" className="share-reply-btn" onClick={() => startQuote(s)}>
                  {intl.formatMessage({ id: "reading.replyQuote" })}
                </button>
              </div>
              {s.quote && <div className="share-quote">{s.quote}</div>}
              <div className="share-text">{s.text}</div>
            </div>
          ))
        )}
      </div>
      <div style={{ marginTop: 12 }}>
        {quoteTarget && (
          <div className="checkin-quote-bar">
            <span className="checkin-quote-text">
              {intl.formatMessage({ id: "reading.quoteOf" }, { author: quoteTarget.author })}：{quoteTarget.text}
            </span>
            <button type="button" className="checkin-quote-cancel" onClick={() => setQuoteTarget(null)}>
              {intl.formatMessage({ id: "reading.cancelQuote" })}
            </button>
          </div>
        )}
        <Input.TextArea
          ref={shareInputRef}
          rows={3}
          maxLength={500}
          placeholder={intl.formatMessage({ id: "reading.sharePlaceholder" })}
          value={text}
          disabled={!loggedIn}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="primary" style={{ marginTop: 8 }} disabled={!loggedIn} onClick={handleShare}>
          {intl.formatMessage({ id: "reading.shareSubmit" })}
        </Button>
      </div>
    </div>
  );
};

export default CheckinPanel;
